import type { IndicatorCategory } from '@/lib/lytick/chart-config';
import type { IndicatorFigureStyle, IndicatorInstance, IndicatorSource } from '@/types/indicator';

export interface IndicatorCatalogEntry {
  name: string;               // KlineCharts indicator name (exact string)
  displayName: string;        // Human-readable label
  shortName: string;          // e.g. 'SMA', 'BB'
  description: string;        // One-sentence explanation
  category: IndicatorCategory;
  isOverlay: boolean;
  defaultCalcParams: unknown[];
  calcParamLabels: string[];
  calcParamTypes: ('number' | 'source' | 'multiplier')[];
  calcParamRanges?: { min: number; max: number; step: number }[];
  defaultSource?: IndicatorSource;
  defaultFigureStyles: Record<string, IndicatorFigureStyle>;
  figureLabels: Record<string, string>;
  // Maps each figure key to its KlineCharts render type.
  // Used to build the positional lines[]/bars[]/circles[] style arrays.
  figureTypes: Record<string, 'line' | 'bar' | 'circle'>;
  isCustom?: boolean;
}

const GOLD = '#F5A623';
const CYAN = '#2196F3';
const MAGENTA = '#E040FB';
const GREEN = '#26A69A';
const RED = '#EF5350';
const GRAY = '#787B86';
const PURPLE = '#AB47BC';
const LIGHT_GRAY = 'rgba(120,123,134,0.4)';

export const INDICATOR_CATALOG: IndicatorCatalogEntry[] = [
  // ── Trend ──────────────────────────────────────────────────────────
  {
    name: 'MA',
    displayName: 'Moving Average',
    shortName: 'MA',
    description: 'Smooth price data to identify trends',
    category: 'trend',
    isOverlay: true,
    defaultCalcParams: [5, 10, 30, 60],
    calcParamLabels: ['MA 1', 'MA 2', 'MA 3', 'MA 4'],
    calcParamTypes: ['number', 'number', 'number', 'number'],
    calcParamRanges: undefined,
    defaultSource: 'close',
    defaultFigureStyles: {
      ma1: { color: GOLD, lineWidth: 1, lineStyle: 'solid', visible: true },
      ma2: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
      ma3: { color: MAGENTA, lineWidth: 1, lineStyle: 'solid', visible: true },
      ma4: { color: GREEN, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { ma1: 'MA 1', ma2: 'MA 2', ma3: 'MA 3', ma4: 'MA 4' },
    figureTypes: { ma1: 'line', ma2: 'line', ma3: 'line', ma4: 'line' },
  },
  {
    name: 'SMA',
    displayName: 'Simple Moving Average',
    shortName: 'SMA',
    description: 'Simple moving average for baseline trend direction',
    category: 'trend',
    isOverlay: true,
    defaultCalcParams: [20],
    calcParamLabels: ['Period'],
    calcParamTypes: ['number'],
    calcParamRanges: [{ min: 1, max: 500, step: 1 }],
    defaultSource: 'close',
    defaultFigureStyles: {
      sma: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { sma: 'SMA' },
    figureTypes: { sma: 'line' },
  },
  {
    name: 'EMA',
    displayName: 'Exponential Moving Average',
    shortName: 'EMA',
    description: 'Faster moving average that weights recent price action',
    category: 'trend',
    isOverlay: true,
    defaultCalcParams: [12, 26],
    calcParamLabels: ['EMA 1', 'EMA 2'],
    calcParamTypes: ['number', 'number'],
    calcParamRanges: [{ min: 1, max: 500, step: 1 }, { min: 1, max: 500, step: 1 }],
    defaultSource: 'close',
    defaultFigureStyles: {
      ema1: { color: GOLD, lineWidth: 1, lineStyle: 'solid', visible: true },
      ema2: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { ema1: 'EMA 1', ema2: 'EMA 2' },
    figureTypes: { ema1: 'line', ema2: 'line' },
  },
  {
    name: 'SAR',
    displayName: 'Parabolic SAR',
    shortName: 'SAR',
    description: 'Trend reversal dots',
    category: 'trend',
    isOverlay: true,
    defaultCalcParams: [2, 2, 20],
    calcParamLabels: ['Step', 'Max Step', 'Start'],
    calcParamTypes: ['multiplier', 'multiplier', 'number'],
    calcParamRanges: [
      { min: 0.01, max: 1, step: 0.01 },
      { min: 0.01, max: 1, step: 0.01 },
      { min: 1, max: 100, step: 1 },
    ],
    defaultFigureStyles: {
      sar: { color: CYAN, visible: true },
    },
    figureLabels: { sar: 'SAR' },
    figureTypes: { sar: 'circle' },
  },
  {
    name: 'BBI',
    displayName: 'Bull and Bear Index',
    shortName: 'BBI',
    description: 'Combines multiple moving averages to signal trend direction',
    category: 'trend',
    isOverlay: true,
    defaultCalcParams: [3, 6, 12, 24],
    calcParamLabels: ['MA 1', 'MA 2', 'MA 3', 'MA 4'],
    calcParamTypes: ['number', 'number', 'number', 'number'],
    defaultSource: 'close',
    defaultFigureStyles: {
      bbi: { color: MAGENTA, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { bbi: 'BBI' },
    figureTypes: { bbi: 'line' },
  },
  {
    name: 'DMI',
    displayName: 'ADX / DMI',
    shortName: 'DMI',
    description: 'Average Directional Index for trend strength measurement',
    category: 'trend',
    isOverlay: false,
    defaultCalcParams: [14, 6],
    calcParamLabels: ['DI Period', 'ADX Smoothing'],
    calcParamTypes: ['number', 'number'],
    calcParamRanges: [{ min: 1, max: 100, step: 1 }, { min: 1, max: 100, step: 1 }],
    defaultFigureStyles: {
      pdi: { color: GREEN, lineWidth: 1, lineStyle: 'solid', visible: true },
      mdi: { color: RED, lineWidth: 1, lineStyle: 'solid', visible: true },
      adx: { color: GOLD, lineWidth: 2, lineStyle: 'solid', visible: true },
      adxr: { color: CYAN, lineWidth: 1, lineStyle: 'dashed', visible: true },
    },
    figureLabels: { pdi: '+DI', mdi: '-DI', adx: 'ADX', adxr: 'ADXR' },
    figureTypes: { pdi: 'line', mdi: 'line', adx: 'line', adxr: 'line' },
  },
  {
    name: 'TRIX',
    displayName: 'TRIX',
    shortName: 'TRIX',
    description: 'Triple exponentially smoothed rate of change',
    category: 'trend',
    isOverlay: false,
    defaultCalcParams: [12, 9],
    calcParamLabels: ['Period', 'Signal Period'],
    calcParamTypes: ['number', 'number'],
    calcParamRanges: [{ min: 1, max: 200, step: 1 }, { min: 1, max: 200, step: 1 }],
    defaultSource: 'close',
    defaultFigureStyles: {
      trix: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
      maTrix: { color: GOLD, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { trix: 'TRIX', maTrix: 'Signal' },
    figureTypes: { trix: 'line', maTrix: 'line' },
  },
  {
    name: 'DMA',
    displayName: 'DMA',
    shortName: 'DMA',
    description: 'Dual moving average for trend crossover signals',
    category: 'trend',
    isOverlay: false,
    defaultCalcParams: [10, 50, 10],
    calcParamLabels: ['Fast MA', 'Slow MA', 'Signal Period'],
    calcParamTypes: ['number', 'number', 'number'],
    defaultSource: 'close',
    defaultFigureStyles: {
      dma: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
      ama: { color: GOLD, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { dma: 'DMA', ama: 'AMA' },
    figureTypes: { dma: 'line', ama: 'line' },
  },
  {
    name: 'LYTICK_SUPERTREND',
    displayName: 'SuperTrend',
    shortName: 'ST',
    description: 'Trend-following indicator using ATR to set dynamic stop levels',
    category: 'trend',
    isOverlay: true,
    isCustom: true,
    defaultCalcParams: [10, 3],
    calcParamLabels: ['ATR Period', 'Multiplier'],
    calcParamTypes: ['number', 'multiplier'],
    calcParamRanges: [{ min: 1, max: 50, step: 1 }, { min: 0.1, max: 10, step: 0.1 }],
    defaultFigureStyles: {
      supertrend: { color: GREEN, lineWidth: 2, lineStyle: 'solid', visible: true },
    },
    figureLabels: { supertrend: 'SuperTrend' },
    figureTypes: { supertrend: 'line' },
  },
  {
    name: 'LYTICK_ICHIMOKU',
    displayName: 'Ichimoku Cloud',
    shortName: 'ICH',
    description: 'Multi-component trend system with cloud support/resistance',
    category: 'trend',
    isOverlay: true,
    isCustom: true,
    defaultCalcParams: [9, 26, 52, 26],
    calcParamLabels: ['Conversion', 'Base', 'Lagging Span', 'Displacement'],
    calcParamTypes: ['number', 'number', 'number', 'number'],
    calcParamRanges: [
      { min: 1, max: 200, step: 1 },
      { min: 1, max: 200, step: 1 },
      { min: 1, max: 400, step: 1 },
      { min: 1, max: 200, step: 1 },
    ],
    defaultFigureStyles: {
      tenkan: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
      kijun: { color: RED, lineWidth: 1, lineStyle: 'solid', visible: true },
      senkouA: { color: GREEN, lineWidth: 1, lineStyle: 'solid', visible: true },
      senkouB: { color: RED, lineWidth: 1, lineStyle: 'solid', visible: true },
      chikou: { color: MAGENTA, lineWidth: 1, lineStyle: 'dashed', visible: true },
    },
    figureLabels: {
      tenkan: 'Tenkan-sen',
      kijun: 'Kijun-sen',
      senkouA: 'Senkou Span A',
      senkouB: 'Senkou Span B',
      chikou: 'Chikou Span',
    },
    figureTypes: { tenkan: 'line', kijun: 'line', senkouA: 'line', senkouB: 'line', chikou: 'line' },
  },
  {
    name: 'LYTICK_PIVOTS',
    displayName: 'Pivot Points',
    shortName: 'PIVOTS',
    description: 'Classic floor trader pivot points for support and resistance levels',
    category: 'trend',
    isOverlay: true,
    isCustom: true,
    defaultCalcParams: [],
    calcParamLabels: [],
    calcParamTypes: [],
    defaultFigureStyles: {
      pivot: { color: GOLD, lineWidth: 2, lineStyle: 'solid', visible: true },
      r1: { color: GREEN, lineWidth: 1, lineStyle: 'dashed', visible: true },
      r2: { color: GREEN, lineWidth: 1, lineStyle: 'dashed', visible: true },
      r3: { color: GREEN, lineWidth: 1, lineStyle: 'dashed', visible: true },
      s1: { color: RED, lineWidth: 1, lineStyle: 'dashed', visible: true },
      s2: { color: RED, lineWidth: 1, lineStyle: 'dashed', visible: true },
      s3: { color: RED, lineWidth: 1, lineStyle: 'dashed', visible: true },
    },
    figureLabels: {
      pivot: 'Pivot',
      r1: 'R1',
      r2: 'R2',
      r3: 'R3',
      s1: 'S1',
      s2: 'S2',
      s3: 'S3',
    },
    figureTypes: { pivot: 'line', r1: 'line', r2: 'line', r3: 'line', s1: 'line', s2: 'line', s3: 'line' },
  },

  // ── Momentum ────────────────────────────────────────────────────────
  {
    name: 'RSI',
    displayName: 'Relative Strength Index',
    shortName: 'RSI',
    description: 'Overbought and oversold structure',
    category: 'momentum',
    isOverlay: false,
    defaultCalcParams: [14, 30, 70],
    calcParamLabels: ['Period', 'Oversold', 'Overbought'],
    calcParamTypes: ['number', 'number', 'number'],
    calcParamRanges: [{ min: 1, max: 200, step: 1 }],
    defaultSource: 'close',
    defaultFigureStyles: {
      rsi1: { color: PURPLE, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { rsi1: 'RSI' },
    figureTypes: { rsi1: 'line' },
  },
  {
    name: 'MACD',
    displayName: 'MACD',
    shortName: 'MACD',
    description: 'Trend momentum with fast and slow moving averages',
    category: 'momentum',
    isOverlay: false,
    defaultCalcParams: [12, 26, 9],
    calcParamLabels: ['Fast Length', 'Slow Length', 'Signal Length'],
    calcParamTypes: ['number', 'number', 'number'],
    calcParamRanges: [
      { min: 1, max: 200, step: 1 },
      { min: 1, max: 200, step: 1 },
      { min: 1, max: 200, step: 1 },
    ],
    defaultSource: 'close',
    defaultFigureStyles: {
      dif: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
      dea: { color: GOLD, lineWidth: 1, lineStyle: 'solid', visible: true },
      macd: { color: RED, visible: true },
    },
    figureLabels: { dif: 'MACD', dea: 'Signal', macd: 'Histogram' },
    figureTypes: { dif: 'line', dea: 'line', macd: 'bar' },
  },
  {
    name: 'KDJ',
    displayName: 'KDJ',
    shortName: 'KDJ',
    description: 'Stochastic-style oscillator tuned for swing momentum',
    category: 'momentum',
    isOverlay: false,
    defaultCalcParams: [9, 3, 3],
    calcParamLabels: ['K Period', 'K Smooth', 'D Smooth'],
    calcParamTypes: ['number', 'number', 'number'],
    defaultFigureStyles: {
      k: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
      d: { color: GOLD, lineWidth: 1, lineStyle: 'solid', visible: true },
      j: { color: MAGENTA, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { k: 'K', d: 'D', j: 'J' },
    figureTypes: { k: 'line', d: 'line', j: 'line' },
  },
  {
    name: 'LYTICK_STOCH',
    displayName: 'Stochastic',
    shortName: 'STOCH',
    description: 'Momentum oscillator with %K and %D',
    category: 'momentum',
    isOverlay: false,
    isCustom: true,
    defaultCalcParams: [14, 3],
    calcParamLabels: ['%K Period', '%D Period'],
    calcParamTypes: ['number', 'number'],
    calcParamRanges: [{ min: 1, max: 200, step: 1 }, { min: 1, max: 200, step: 1 }],
    defaultFigureStyles: {
      k: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
      d: { color: GOLD, lineWidth: 1, lineStyle: 'dashed', visible: true },
    },
    figureLabels: { k: '%K', d: '%D' },
    figureTypes: { k: 'line', d: 'line' },
  },
  {
    name: 'CCI',
    displayName: 'Commodity Channel Index',
    shortName: 'CCI',
    description: 'Mean-reversion extremes',
    category: 'momentum',
    isOverlay: false,
    defaultCalcParams: [14],
    calcParamLabels: ['Period'],
    calcParamTypes: ['number'],
    calcParamRanges: [{ min: 1, max: 200, step: 1 }],
    defaultSource: 'close',
    defaultFigureStyles: {
      cci: { color: PURPLE, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { cci: 'CCI' },
    figureTypes: { cci: 'line' },
  },
  {
    name: 'WR',
    displayName: 'Williams %R',
    shortName: 'WR',
    description: 'Range-based momentum oscillator for entry timing',
    category: 'momentum',
    isOverlay: false,
    defaultCalcParams: [14],
    calcParamLabels: ['Period'],
    calcParamTypes: ['number'],
    calcParamRanges: [{ min: 1, max: 200, step: 1 }],
    defaultSource: 'close',
    defaultFigureStyles: {
      wr1: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { wr1: '%R' },
    figureTypes: { wr1: 'line' },
  },
  {
    name: 'BIAS',
    displayName: 'BIAS',
    shortName: 'BIAS',
    description: 'Deviation ratio showing how far price strays from its average',
    category: 'momentum',
    isOverlay: false,
    defaultCalcParams: [6, 12, 24],
    calcParamLabels: ['BIAS 1', 'BIAS 2', 'BIAS 3'],
    calcParamTypes: ['number', 'number', 'number'],
    defaultSource: 'close',
    defaultFigureStyles: {
      bias1: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
      bias2: { color: GOLD, lineWidth: 1, lineStyle: 'solid', visible: true },
      bias3: { color: MAGENTA, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { bias1: 'BIAS 1', bias2: 'BIAS 2', bias3: 'BIAS 3' },
    figureTypes: { bias1: 'line', bias2: 'line', bias3: 'line' },
  },
  {
    name: 'AO',
    displayName: 'Awesome Oscillator',
    shortName: 'AO',
    description: 'Momentum indicator comparing recent and medium-term moving averages',
    category: 'momentum',
    isOverlay: false,
    defaultCalcParams: [5, 34],
    calcParamLabels: ['Fast Period', 'Slow Period'],
    calcParamTypes: ['number', 'number'],
    calcParamRanges: [{ min: 1, max: 200, step: 1 }, { min: 1, max: 200, step: 1 }],
    defaultFigureStyles: {
      ao: { color: GREEN, visible: true },
    },
    figureLabels: { ao: 'AO' },
    figureTypes: { ao: 'bar' },
  },
  {
    name: 'ROC',
    displayName: 'Rate of Change',
    shortName: 'ROC',
    description: 'Percentage price change over a lookback period',
    category: 'momentum',
    isOverlay: false,
    defaultCalcParams: [12, 6],
    calcParamLabels: ['Period', 'Signal Period'],
    calcParamTypes: ['number', 'number'],
    defaultSource: 'close',
    defaultFigureStyles: {
      roc: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
      maRoc: { color: GOLD, lineWidth: 1, lineStyle: 'dashed', visible: true },
    },
    figureLabels: { roc: 'ROC', maRoc: 'Signal' },
    figureTypes: { roc: 'line', maRoc: 'line' },
  },
  {
    name: 'MTM',
    displayName: 'Momentum',
    shortName: 'MTM',
    description: 'Raw price momentum measuring absolute price change',
    category: 'momentum',
    isOverlay: false,
    defaultCalcParams: [12, 6],
    calcParamLabels: ['Period', 'Signal Period'],
    calcParamTypes: ['number', 'number'],
    defaultSource: 'close',
    defaultFigureStyles: {
      mtm: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
      maMtm: { color: GOLD, lineWidth: 1, lineStyle: 'dashed', visible: true },
    },
    figureLabels: { mtm: 'MTM', maMtm: 'Signal' },
    figureTypes: { mtm: 'line', maMtm: 'line' },
  },
  {
    name: 'PSY',
    displayName: 'Psychological Line',
    shortName: 'PSY',
    description: 'Ratio of rising candles measuring market sentiment',
    category: 'momentum',
    isOverlay: false,
    defaultCalcParams: [12, 6],
    calcParamLabels: ['Period', 'Signal Period'],
    calcParamTypes: ['number', 'number'],
    defaultSource: 'close',
    defaultFigureStyles: {
      psy: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
      maPsy: { color: GOLD, lineWidth: 1, lineStyle: 'dashed', visible: true },
    },
    figureLabels: { psy: 'PSY', maPsy: 'Signal' },
    figureTypes: { psy: 'line', maPsy: 'line' },
  },

  // ── Volatility ──────────────────────────────────────────────────────
  {
    name: 'BOLL',
    displayName: 'Bollinger Bands',
    shortName: 'BB',
    description: 'Dynamic volatility bands around moving average price',
    category: 'volatility',
    isOverlay: true,
    defaultCalcParams: [20, 2],
    calcParamLabels: ['Period', 'Std Dev'],
    calcParamTypes: ['number', 'multiplier'],
    calcParamRanges: [{ min: 1, max: 200, step: 1 }, { min: 0.1, max: 5, step: 0.1 }],
    defaultSource: 'close',
    defaultFigureStyles: {
      up: { color: GRAY, lineWidth: 1, lineStyle: 'solid', visible: true },
      mid: { color: GRAY, lineWidth: 1, lineStyle: 'solid', visible: true },
      dn: { color: GRAY, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { up: 'Upper', mid: 'Middle', dn: 'Lower' },
    figureTypes: { up: 'line', mid: 'line', dn: 'line' },
  },
  {
    name: 'LYTICK_ATR',
    displayName: 'Average True Range',
    shortName: 'ATR',
    description: 'Average True Range volatility measure',
    category: 'volatility',
    isOverlay: false,
    isCustom: true,
    defaultCalcParams: [14],
    calcParamLabels: ['Period'],
    calcParamTypes: ['number'],
    calcParamRanges: [{ min: 1, max: 200, step: 1 }],
    defaultFigureStyles: {
      atr: { color: CYAN, lineWidth: 1, lineStyle: 'solid', visible: true },
    },
    figureLabels: { atr: 'ATR' },
    figureTypes: { atr: 'line' },
  },

];

// ── Lookup helpers ──────────────────────────────────────────────────────

const _catalogByName = new Map(INDICATOR_CATALOG.map(e => [e.name, e]));

export function getCatalogEntry(name: string): IndicatorCatalogEntry | undefined {
  return _catalogByName.get(name);
}

export const OVERLAY_NAMES = new Set(
  INDICATOR_CATALOG.filter(e => e.isOverlay).map(e => e.name),
);

// ── Style translation ───────────────────────────────────────────────────
// Converts our figureStyles map (keyed by name) into KlineCharts' positional
// lines[]/bars[]/circles[] arrays. Figure keys are iterated in figureLabels
// order, which matches the order figures were declared in registerIndicator.

/**
 * Returns the maximum lookback (bars) needed across all active indicators so
 * the context fetch can guarantee clean signal values from candle 1 of replay.
 */
export function getMaxIndicatorLookback(indicators: IndicatorInstance[]): number {
  let max = 0;

  for (const ind of indicators) {
    const params = ind.calcParams;
    const numParam = (i: number): number =>
      typeof params[i] === 'number' ? (params[i] as number) : 0;

    let lookback: number;
    switch (ind.name) {
      case 'MACD':
        lookback = 34;
        break;
      case 'BOLL':
        lookback = numParam(0) || 20;
        break;
      case 'EMA': {
        const periods = params.filter((v): v is number => typeof v === 'number');
        const maxPeriod = periods.length > 0 ? Math.max(...periods) : 26;
        lookback = Math.max(maxPeriod * 2, 50);
        break;
      }
      case 'MA':
      case 'SMA':
      case 'BBI': {
        const periods = params.filter((v): v is number => typeof v === 'number');
        lookback = periods.length > 0 ? Math.max(...periods) : 50;
        break;
      }
      case 'RSI':
        lookback = 14;
        break;
      case 'LYTICK_ICHIMOKU':
        lookback = 52;
        break;
      case 'DMI':
        lookback = 28;
        break;
      case 'LYTICK_SUPERTREND':
        lookback = 20;
        break;
      case 'LYTICK_PIVOTS':
        lookback = 1;
        break;
      default:
        lookback = 50;
    }

    max = Math.max(max, lookback);
  }

  return Math.max(max, 50);
}

export function buildIndicatorStyles(
  figureStyles: Record<string, IndicatorFigureStyle>,
  catalog: IndicatorCatalogEntry,
): Record<string, unknown> {
  const lines: Record<string, unknown>[] = [];
  const bars: Record<string, unknown>[] = [];
  const circles: Record<string, unknown>[] = [];

  for (const key of Object.keys(catalog.figureLabels)) {
    const type = catalog.figureTypes[key] ?? 'line';
    const s = figureStyles[key] ?? {};
    const color = s.visible === false ? 'transparent' : (s.color ?? undefined);

    if (type === 'line') {
      lines.push({
        color,
        size: s.lineWidth,
        style: s.lineStyle === 'dotted' ? 'dashed' : s.lineStyle,
      });
    } else if (type === 'bar') {
      bars.push({ upColor: color, downColor: color, noChangeColor: color });
    } else {
      circles.push({ upColor: color, downColor: color, noChangeColor: color });
    }
  }

  return { lines, bars, circles };
}
